import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import type { AlbumCaseAddInterface } from '../models/album-case-add.interface';
import type { AlbumCaseInterface, AlbumCaseUpdateInterface, AlbumWithCaseInterface } from '../models/album-case.interface';
import { AlbumCaseTypeEnum } from '../models/album-case.interface';
import type { GetAlbumsQueryInterface } from './album.service';
import { GetAlbumsQuery, GetAlbumsQueryVariables } from './album.service';
import { type DialogParams, ModalDialogService } from '../components/modal-dialog/modal-dialog.service';
import type {
  AlbumCasesAddDialogParamInterface
} from '../components/album-cases-add-dialog/album-cases-add-dialog.component';
import {
  AlbumCasesAddDialogComponent
} from '../components/album-cases-add-dialog/album-cases-add-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from './snackbar.service';
import { SexEnum, SterilisationEnum } from '../models/patient.interface';

export const GetAlbumCasesQuery = gql`query albumCases ($albumId: Long!) {
  albumCases (albumId: $albumId) {
    id
    createdAt
    name
    type
    cases {
      id
      name
      type
      createdAt
      nbAttachments
      study {
        id
        publicId
        createdAt
        previewHash
        previewPublicId
        patientName
        patientSex
        patientSterilisation
        nbInstances
        clinicId
        clinicName
        keywords {
          id
          label
          clinicId
          clinicName
        }
        nbReports
        nbAttachments
        speciesName
        modalityTypeValues
        isNewOrUploading
        studyStatus
        series {
          id
          serieStatus
        }
      }
    }
  }
}`;

@Injectable({
  providedIn: 'root'
})
export class AlbumCaseService {
  constructor(
    private readonly apollo: Apollo,
    private readonly http: HttpClient,
    private readonly translateService: TranslateService,
    private readonly snackbarService: SnackbarService,
    private readonly modalDialogService: ModalDialogService<AlbumCaseAddInterface | undefined, AlbumCasesAddDialogParamInterface>) {
  }

  openCreateModal(paramsModal: AlbumCasesAddDialogParamInterface): void {
    const params: DialogParams<AlbumCasesAddDialogParamInterface> = {
      input: paramsModal,
      config: {
        title: this.translateService.instant('album_case.modal.add.title'),
        showSaveButton: true,
        showCancelButton: true,
        cancelButton: {
          label: this.translateService.instant('album_case.modal.add.cancel_button'),
          icon: ''
        },
        saveButton: {
          label: this.translateService.instant('album_case.modal.add.save_button'),
          icon: ''
        }
      }
    };

    this.modalDialogService.openDialog(params, AlbumCasesAddDialogComponent).subscribe(v => {
      if (v) {
        this.addAlbumCase(v).subscribe(_res => {
          this.snackbarService.openSnackBar(this.translateService.instant('album_cases.added') as string);
        });
      }
    });
  }

  getAlbumCases(albumId: number): Observable<AlbumWithCaseInterface> {
    return this.apollo.query<{ albumCases: AlbumWithCaseInterface }>({
      query: GetAlbumCasesQuery,
      variables: { albumId }
    }).pipe(map(result => result.data.albumCases));
  }

  addAlbumCase(albumCaseAdd: AlbumCaseAddInterface): Observable<AlbumCaseInterface | undefined> {
    return this.apollo.mutate<{ addAlbumCase: AlbumCaseInterface }>({
      mutation: gql`mutation addAlbumCase($dto: AlbumCaseAddDtoInput) {
        addAlbumCase(dto: $dto) {
          id
          name
          type
          createdAt
          nbAttachments
          study {
            id
            publicId
            previewPublicId
            patientName
            patientSex
            nbInstances
            clinicId
            clinicName
            keywords {
              id
              label
              clinicId
              clinicName
            }
            nbReports
            nbAttachments
            speciesName
            modalityTypeValues
            isNewOrUploading
            studyStatus
          }
        }
      }`,
      variables: {
        dto: {
          albumId: albumCaseAdd.albumId,
          type: AlbumCaseTypeEnum[albumCaseAdd.type].toUpperCase(),
          name: albumCaseAdd.name,
          instances: albumCaseAdd.instances,
          keywords: albumCaseAdd.keywords,
          anonymize: albumCaseAdd.anonymize,
          cloneAttachment: albumCaseAdd.cloneAttachment,
          speciesId : albumCaseAdd.speciesId,
          sex : SexEnum[albumCaseAdd.sex].toUpperCase(),
          sterilisation : SterilisationEnum[albumCaseAdd.sterilisation].toUpperCase()
        }
      },
      refetchQueries: [{
        query: GetAlbumCasesQuery,
        variables: { albumId: albumCaseAdd.albumId }
      },
      {
        query: GetAlbumsQuery,
        variables: GetAlbumsQueryVariables
      }],
      update: (store, { data }) => {
        // Read the data from our cache for this query.
        const albumCasesQuery = store.readQuery<GetAlbumCasesQueryInterface>({ query: GetAlbumCasesQuery, variables: { albumId: albumCaseAdd.albumId } });
        if (albumCasesQuery && data) {
          const albumCases = { ...albumCasesQuery.albumCases };
          let cases = (albumCases.cases).map(albumCases => ({ ...albumCases }));

          cases.push(data.addAlbumCase);
          cases = cases.sort((a, b) => a.name.localeCompare(b.name));
          albumCases.cases = cases;
          store.writeQuery({ query: GetAlbumCasesQuery, data: { albumCases } });

          // mise à jour album (nbCases)

          const albumsQuery = store.readQuery<GetAlbumsQueryInterface>({ query: GetAlbumsQuery, variables: GetAlbumsQueryVariables });
          if (albumsQuery) {
            const albums = [...albumsQuery.albums];
            const album = albums.find(k => k.id === albumCaseAdd.albumId);

            if (album) {
              album.nbCases = cases.length;
              store.writeQuery<GetAlbumsQueryInterface>({
                query: GetAlbumsQuery,
                data: {
                  albums
                },
                variables: GetAlbumsQueryVariables
              });
            }
          }
        }
      },
    }).pipe(map(res => res.data ? res.data?.addAlbumCase : undefined));
  }

  deleteAlbumCase(albumCaseId: number, albumId: number): Observable<AlbumCaseInterface | undefined> {
    return this.apollo.mutate<{ deleteAlbumCase: AlbumCaseInterface }>({
      mutation: gql`mutation deleteAlbumCase($id: Long!) {
        deleteAlbumCase(id: $id) {
          id
          createdAt
          type
          name
        }
      }`,
      variables: {
        id: albumCaseId
      },
      refetchQueries: [{
        query: GetAlbumCasesQuery,
        variables: { albumId }
      }, {
        query: GetAlbumsQuery,
        variables: GetAlbumsQueryVariables
      }],
      update: (store, { data }) => {
        const albumCasesQuery = store.readQuery<GetAlbumCasesQueryInterface>({ query: GetAlbumCasesQuery, variables: { albumId } });
        if (albumCasesQuery) {
          const albumCases = { ...albumCasesQuery.albumCases };
          if (data) {
            const cases = (albumCases.cases).map(albumCases => ({ ...albumCases })).filter(c => c.id !== albumCaseId);

            albumCases.cases = cases;
            store.writeQuery({ query: GetAlbumCasesQuery, data: { albumCases } });

            // mise à jour album (nbCases)

            const albumsQuery = store.readQuery<GetAlbumsQueryInterface>({ query: GetAlbumsQuery, variables: GetAlbumsQueryVariables });
            if (albumsQuery) {
              const albums = [...albumsQuery.albums];
              const album = albums.find(k => k.id === albumId);

              if (album) {
                album.nbCases = cases.length;
                store.writeQuery<GetAlbumsQueryInterface>({
                  query: GetAlbumsQuery,
                  data: {
                    albums
                  },
                  variables: GetAlbumsQueryVariables
                });
              }
            }
          }
        }
      },
    }).pipe(map(res => res.data ? res.data?.deleteAlbumCase : undefined));
  }

  updateAlbumCase(files: File[], albumCaseUpdate: AlbumCaseUpdateInterface): Observable<AlbumCaseInterface | undefined> {

    return this.apollo.mutate<{ updateAlbumCase: AlbumCaseInterface }>({
      mutation: gql`mutation updateAlbumCase ($files: [Upload!], $dto: AlbumCaseUpdateDtoInput) {
        updateAlbumCase(files: $files, dto: $dto) {
          id
          name
          createdAt
          albumId
          type
          nbAttachments
        }
      }`,
      variables: {
        files,
        dto: {
          id: albumCaseUpdate.id,
          type: AlbumCaseTypeEnum[albumCaseUpdate.type].toUpperCase(),
          name: albumCaseUpdate.name,
          fileTypes: albumCaseUpdate.fileTypes
        }
      },
      context: {
        useMultipart: true
      },
    }).pipe(map(res => res.data ? res.data?.updateAlbumCase : undefined));
  }
}

export interface GetAlbumCasesQueryInterface {
  albumCases: AlbumCasesInterface;
}

interface AlbumCasesInterface {
  cases: AlbumCaseInterface[];
}

export interface GraphqlDataInterface<T> {
  data: T;
}
